import { useMemo } from 'react';
import { SingleProfitCalculationApiResponse } from '../../../ProfitCalculations/types';
import { TimeFrame } from '../../../../types/Bot';
import { useFetchHeikinCandlesQuery } from '../../../../store/services/api';
import { getTimeFrameIntervalTimestamp } from '../../../Detail/Charts/PriceChart/helpers';
import { toDisplayData } from './adapter';
import { ResultProvider } from './context';
import Result from './Result';
import './styles.css';

interface Props {
  data: SingleProfitCalculationApiResponse;
}

/**
 * Renders the SPC result page for a given calculation response. This is the
 * drop-in used everywhere an SPC result is shown — the subscription
 * Calculations tab and the ProfitCalculations screens.
 *
 * The SPC response usually omits the (heavy) shorter-timeframe candle arrays, so
 * the candle charts would have nothing to draw. When they're missing we fetch
 * them and inject them; when the response already carries them this is a no-op.
 */
const SpcResultView = ({ data }: Props) => {
  const hasCandles = !!data.savedData?.shorterTNormalCandles?.length;

  const candleParams = useMemo(() => {
    if (hasCandles || !data.shorterTimeFrame || !data.exchange) {
      return undefined;
    }
    const start = new Date(data.startTimestamp).valueOf();
    const end = new Date(data.endTimestamp).valueOf();
    if (!start || !end) {
      return undefined;
    }
    return {
      baseAsset: data.baseAsset,
      quoteAsset: data.quoteAsset,
      timeFrame: data.shorterTimeFrame,
      startTimestamp:
        start -
        getTimeFrameIntervalTimestamp(data.shorterTimeFrame as TimeFrame) * 3,
      endTimestamp: end,
      includeLatestCandle: true,
      exchange: data.exchange,
    };
  }, [data, hasCandles]);

  const { data: fetchedCandles } = useFetchHeikinCandlesQuery(candleParams, {
    skip: !candleParams,
  });

  const displayData = useMemo(() => {
    const result = toDisplayData(data);
    if (!hasCandles && fetchedCandles?.normal?.length) {
      result.raw = {
        ...result.raw,
        savedData: {
          ...result.raw.savedData,
          shorterTNormalCandles: [fetchedCandles.normal],
        },
      };
    }
    return result;
  }, [data, hasCandles, fetchedCandles]);

  return (
    <ResultProvider data={displayData}>
      <Result />
    </ResultProvider>
  );
};

export default SpcResultView;
